/**
 * @file src/components/organisms/YieldBearingAssetInvestmentActionsSupportedTokenList/YieldBearingAssetInvestmentActionsSupportedTokenList.tsx
 * @description Displays a list of tokens that are supported for either deposits, withdrawals, or both in a yield-bearing asset.
 *              It checks if tokens are in the user’s wallet based on the presence of wallet-specific properties and uses a table for layout.
 * @module YieldBearingAssetInvestmentActionsSupportedTokenList
 */

import React, { FC, useMemo } from 'react';
import { YieldBearingAssetInterface } from '@/interfaces/YieldBearingAssetInterface';
import { TokenInterface, WalletTokenInterface } from '@/interfaces';
import { TableRowProps } from '@/components/molecules/Table/interfaces/Table';
import { Table, TokenListItem, WalletTokenListItem } from '@/components';

interface YieldBearingAssetInvestmentActionsSupportedTokenListProps {
  yieldBearingAsset: YieldBearingAssetInterface;
  showFor: 'deposit' | 'withdrawal' | 'both';
  showNetwork?: boolean;
  showNetworkPrefix?: boolean;
  showTicker?: boolean;
}

const YieldBearingAssetInvestmentActionsSupportedTokenList: FC<
  YieldBearingAssetInvestmentActionsSupportedTokenListProps
> = ({
  yieldBearingAsset,
  showFor,
  showNetwork = true,
  showNetworkPrefix = true,
  showTicker = false,
}) => {
  // Determine which tokens to display based on showFor prop
  const tokensToDisplay = useMemo(() => {
    const tokens: (TokenInterface | WalletTokenInterface)[] = [];

    if (showFor === 'deposit' || showFor === 'both') {
      tokens.push(...(yieldBearingAsset.tokensSupportedForDeposit || []));
    }
    if (showFor === 'withdrawal' || showFor === 'both') {
      tokens.push(...yieldBearingAsset.tokensSupportedForWithdrawal);
    }

    return tokens;
  }, [yieldBearingAsset, showFor]);

  const rows: TableRowProps[] = tokensToDisplay.map((token) => ({
    cells: [
      {
        mainText:
          'walletAddress' in token ? (
            // Display as a wallet token if wallet-specific properties are present
            <WalletTokenListItem
              token={token as WalletTokenInterface}
              showWalletAddress={false}
              showBalance
              showNetwork={showNetwork}
              showNetworkPrefix={showNetworkPrefix}
              showTicker={showTicker}
            />
          ) : (
            // Otherwise, display as a standard token item
            <TokenListItem
              token={token as TokenInterface}
              showNetwork={showNetwork}
              showNetworkPrefix={showNetworkPrefix}
              showTicker={showTicker}
            />
          ),
        align: 'left',
      },
    ],
  }));

  return <>{rows.length > 0 && <Table columnWidths={[100]} rows={rows} />}</>;
};

export default YieldBearingAssetInvestmentActionsSupportedTokenList;
